<!-- Spinner.vue -->
<template>
  <div
    :class="cn('flex aspect-square items-center justify-center', props.class)"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 50 50"
      fill="none"
      :stroke-width="props.strokeWidth"
      class="h-full w-full origin-center text-current"
    >
      <circle
        class="spinner-rotate spinner-base"
        cx="25"
        cy="25"
        :r="props.radius"
        stroke="currentColor"
        stroke-linecap="round"
        fill="none"
        :style="{
          '--circumference': circumference.toFixed(3),
          '--dash': dash.toFixed(3),
          '--gap': gapLen.toFixed(3)
        }"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { cn } from '../lib/utils'

  export interface SpinnerProps {
    class?: string
    strokeWidth?: number
    /**
     * Gap fraction (0..1). 0.2 = 20% open (default).
     */
    gap?: number
    /**
     * Radius inside a 50x50 viewBox (default 20).
     * Keep this consistent with the viewBox / cx/cy if you change it.
     */
    radius?: number
  }

  const props = withDefaults(defineProps<SpinnerProps>(), {
    strokeWidth: 4,
    gap: 0.2,
    radius: 20
  })

  const circumference = computed(() => 2 * Math.PI * props.radius)
  const gapFraction = computed(() => Math.min(Math.max(props.gap, 0), 1))
  const dash = computed(() => (1 - gapFraction.value) * circumference.value)
  const gapLen = computed(() => circumference.value - dash.value)
</script>

<style scoped>
  /* shared */
  .spinner-base {
    transform-origin: 50% 50%;
    /* stroke-linecap already set on element, but keep baseline here if desired */
    stroke-linecap: round;
  }

  /* static open arc that just rotates */
  .spinner-rotate {
    stroke-dasharray: var(--dash) var(--gap);
    stroke-dashoffset: 0;
    animation: spinner-rotate 1s linear infinite;
  }

  @keyframes spinner-rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  /* We reference --circumference inside the keyframes so the animation scales with radius */
  @keyframes spinner-dash {
    0% {
      stroke-dasharray: 1 var(--circumference);
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: calc(var(--circumference) * 0.72) var(--circumference);
      stroke-dashoffset: calc(-1 * var(--circumference) * 0.28);
    }
    100% {
      stroke-dasharray: calc(var(--circumference) * 0.72) var(--circumference);
      stroke-dashoffset: calc(-1 * var(--circumference) * 0.99);
    }
  }
</style>
